import { Group, Loader, Modal, Stack, Text, Title } from '@common/ui'
import { com } from '@eidu/web'
import useResource from '../../../../../io/useResource'
import MergeEditor from '../../../../../components/data/MergeEditor'
import DiffStats from './DiffStats'
import EntityDiffSummary from './EntityDiffSummary'
import { EntitySnapshotDiff } from './entitySnapshotDiff'
import EntityType = com.eidu.entity.type.EntityType

type ResourceDiffDialogProps = {
  onClose: () => void
  leftKey: string | undefined
  rightKey: string | undefined
  leftLabel: string
  rightLabel: string
  title?: string
  entityDiff?: EntitySnapshotDiff
  entityType?: EntityType
}

const ResourceDiffDialog = ({
  onClose,
  leftKey,
  rightKey,
  leftLabel,
  rightLabel,
  title,
  entityDiff,
  entityType,
}: ResourceDiffDialogProps) => {
  const showPayloadDiff = !!leftKey && !!rightKey
  const {
    content: leftContent,
    isLoading: leftLoading,
    error: leftError,
  } = useResource(showPayloadDiff ? leftKey : undefined)
  const {
    content: rightContent,
    isLoading: rightLoading,
    error: rightError,
  } = useResource(showPayloadDiff ? rightKey : undefined)

  const loading = showPayloadDiff && (leftLoading || rightLoading)
  const error = leftError || rightError
  const ready = showPayloadDiff && !loading && !error && leftContent !== undefined && rightContent !== undefined

  return (
    <Modal
      opened
      onClose={onClose}
      size="80rem"
      title={
        <Stack gap={0}>
          <Text size="xs" tt="uppercase" c="dimmed" lts={1}>
            Compare
          </Text>
          <Title order={4}>{title ?? 'Diff'}</Title>
          <Group gap="md" mt="xxs" c="dimmed">
            <Text size="xs">← {leftLabel}</Text>
            <Text size="xs">→ {rightLabel}</Text>
            {ready && <DiffStats left={leftContent} right={rightContent} />}
          </Group>
        </Stack>
      }
      styles={{ body: { height: '70vh', display: 'flex', flexDirection: 'column', padding: 0 } }}
    >
      {entityDiff && (
        <div style={{ padding: 16, borderBottom: '1px solid var(--trellis-color-outline)' }}>
          <Text size="sm" fw={600} mb="xxs">
            Fields & links
          </Text>
          <EntityDiffSummary
            diff={entityDiff}
            entityType={entityType}
            beforeLabel={leftLabel}
            afterLabel={rightLabel}
          />
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Text size="sm" fw={600} px="md" pt="sm" pb="xxs">
          Payload
        </Text>
        {showPayloadDiff ? (
          <>
            {loading && (
              <Stack flex={1} align="center" justify="center">
                <Loader />
              </Stack>
            )}
            {error && (
              <Stack flex={1} align="center" justify="center" px="lg">
                <Text c="negative">Failed to load diff: {error.message}</Text>
              </Stack>
            )}
            {ready && <MergeEditor left={leftContent} right={rightContent} />}
          </>
        ) : (
          <div style={{ paddingInline: 16, paddingBlock: 8 }}>
            <Text size="sm" c="dimmed">
              Payload unchanged.
            </Text>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ResourceDiffDialog
